import React, { useEffect, useState } from "react";
import { ApiHook } from "../../hooks/apiHook";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import ConfirmModal from "../../components/Common/ConfirmModal";
import NewSubscriptionCreator from "../../components/Subscription/NewSubscriptionCreator";
import "./subscription-details.css";
import { setConversionFactors } from "../../store/reducers/userReducer";
import CurrencyConverter from "../../Currency/CurrencyConverter";

const SubscriptionDetails = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState("");
  const [showNewSubscriptionCreator, setShowNewSubscriptionCreator] =
    useState(false);
  const user = useSelector((state) => state.user);
  const userSelectedCurrency = useSelector(
      (state) => state.user?.selectedCurrency
    );

  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } =
    ApiHook.CallSubscriptionDetails();
  const manageSubscriptionMutation = ApiHook.CallManageSubscription();

  const subscriptionData = data?.data;
  const hasActiveSubscription = subscriptionData?.hasActiveSubscription;
  const subscription = subscriptionData?.subscription;
  const subscriptionPlans = subscriptionData?.subscriptionPlans || [];

  // Check if subscription is cancelled
  const isSubscriptionCancelled = subscription?.status === "canceled" || subscription?.status === "cancelled";

  // Action handlers
  const handleAction = (action) => {
    setModalAction(action);
    setShowModal(true);
  };

  const confirmAction = () => {
    manageSubscriptionMutation.mutate(
      { action: modalAction },
      {
        onSuccess: () => {
          setShowModal(false);
          // Refetch subscription details to get updated status
          refetch();
        },
        onError: () => {
          setShowModal(false);
        },
      }
    );
  };

  const handleNewSubscriptionCreated = () => {
    setShowNewSubscriptionCreator(false);
    // Invalidate and refetch subscription details
    queryClient.invalidateQueries(["subscription-details"]);
    refetch();
  };

  const handleCreateNewSubscription = () => {
    setShowNewSubscriptionCreator(true);
  };

  if (isLoading)
    return (
      <div className="subscription-details-container">
        <div className="page_head_top">Subscription Management</div>
        <div className="loading-state">Loading subscription details...</div>
      </div>
    );

  if (isError)
    return (
      <div className="subscription-details-container">
        <div className="page_head_top">Subscription Management</div>
        <div
          className="subscription-card"
          style={{ background: "none !important" }}
        >
          <div className="error-msg">Failed to fetch subscription details</div>
        </div>
      </div>
    );

  // Handle case where user has no subscription at all
  if (!hasActiveSubscription && !subscription) {
    return (
      <div className="subscription-details-container">
        <div className="page_head_top">Subscription Management</div>
        <div className="no-subscription-state">
          <div className="no-subscription-icon">📋</div>
          <h2>No Subscription Found</h2>
          <p>
            You don't have any subscription yet. Create your first subscription
            to get started.
          </p>
          <button
            className="btn create-subscription-btn"
            onClick={handleCreateNewSubscription}
          >
            Create New Subscription
          </button>
        </div>
        {showNewSubscriptionCreator && (
          <NewSubscriptionCreator
            onSubscriptionCreated={handleNewSubscriptionCreated}
          />
        )}
      </div>
    );
  }

  return (
    <div className="subscription-details-container">
      <div className="page_head_top">Subscription Management</div>

      <div className="subscription-card">
        <div className="subscription-header">
          <h1 className="subscription-title">
            {isSubscriptionCancelled
              ? "Cancelled Subscription"
              : "Your Subscription"}
          </h1>
          <p className="subscription-subtitle">
            {isSubscriptionCancelled
              ? "Your subscription has been cancelled. You can create a new one below."
              : "Manage your plan and billing settings"}
          </p>
        </div>

        <div className="subscription-content">
          {isSubscriptionCancelled && (
            <div className="cancelled-subscription-notice">
              <div className="notice-icon">⚠️</div>
              <div className="notice-content">
                <h4>Subscription Cancelled</h4>
                <p>
                  Your subscription was cancelled and is no longer active. You
                  can create a new subscription below to continue using our
                  services.
                </p>
                <button
                  className="btn create-subscription-btn"
                  onClick={handleCreateNewSubscription}
                  style={{ color: "#fff !important" }}
                >
                  Create New Subscription
                </button>
              </div>
            </div>
          )}

          <div className="subscription-grid">
            <div className="subscription-section">
              <h3 className="section-title">Plan Details</h3>

              <div className="subscription-info-item">
                <span className="info-label">Plan ID</span>
                <span className="info-value">{subscription.id}</span>
              </div>
              <div className="subscription-info-item">
                <span className="info-label">Status</span>
                <span className={`status-badge status-${subscription.status}`}>
                  {subscription.status}
                </span>
              </div>

              <div className="subscription-info-item">
                <span className="info-label">Billing Interval</span>
                <span className="info-value">{subscription.interval}ly</span>
              </div>
            </div>

            <div className="subscription-section">
              <h3 className="section-title">Payment Information</h3>
              <div className="subscription-info-item">
                <span className="info-label">Amount</span>
                <span className="info-value">{`${
              userSelectedCurrency?.symbolLeft ? userSelectedCurrency?.symbolLeft : "$"
            } ${CurrencyConverter(
              subscription.amount ?? 0,
              setConversionFactors
            )}`} </span>
              </div>
              <div className="subscription-info-item">
                <span className="info-label">Payment Method</span>
                <span className="info-value">{subscription.provider}</span>
              </div>
            </div>
          </div>

          <div className="subscription-actions">
            {subscription.status === "active" && (
              <>
                <button
                  className="btn pause"
                  onClick={() => handleAction("pause")}
                  aria-label="Pause Subscription"
                >
                  Pause Subscription
                </button>
                <button
                  className="btn cancel"
                  onClick={() => handleAction("cancel")}
                  aria-label="Cancel Subscription"
                >
                  Cancel Subscription
                </button>
              </>
            )}
            {subscription.status === "paused" && (
              <button
                className="btn reactivate"
                onClick={() => handleAction("reactivate")}
                aria-label="Reactivate Subscription"
              >
                Reactivate Subscription
              </button>
            )}
          </div>
        </div>
      </div>

      {/* New Subscription Creator for Cancelled Subscriptions */}
      {(isSubscriptionCancelled || showNewSubscriptionCreator) && (
        <NewSubscriptionCreator
          onSubscriptionCreated={handleNewSubscriptionCreated}
          subscriptionPlans={subscriptionPlans}
        />
      )}

      {showModal && (
        <ConfirmModal
          isOpen={showModal}
          title={`Confirm ${
            modalAction.charAt(0).toUpperCase() + modalAction.slice(1)
          }`}
          message={`Are you sure you want to ${modalAction} your subscription? This action cannot be undone.`}
          onConfirm={confirmAction}
          onCancel={() => setShowModal(false)}
          loading={manageSubscriptionMutation.isLoading}
          confirmText={
            modalAction.charAt(0).toUpperCase() + modalAction.slice(1)
          }
        />
      )}
    </div>
  );
};

export default SubscriptionDetails;
